/**
 * Pure ranking for the "Help me choose" recommendation slider (#1844 Slice 3).
 *
 * Turns two things OttoTester already keeps — the per-(agent, model) quality
 * leaderboard (`biAPI.modelComparison`) and the pricing catalog's cost — into a
 * single ranked list at a chosen cost↔performance setting. Kept as a pure,
 * presentation-neutral module (numbers only, the UI formats every string) so it
 * is unit-testable and reused verbatim by the customer surface (#1849).
 *
 * Design notes (see #1844 Slice 3):
 *   - Both axes are **min-max normalized WITHIN the candidate set**, so the
 *     scores are relative to this shortlist, not absolute percentages. A
 *     degenerate axis (one model, or every model equal) contributes the
 *     midpoint (50) rather than dividing by zero.
 *   - **Cost ranks on the continuous blended rate** (`input + 3×output`); the
 *     Low/Medium/High/Premium pill is display only. Same signal, two views —
 *     so two same-tier models still separate cleanly.
 *   - **Cold-start / unscored, unknown ≠ 0.** A model the agent never ran has
 *     `meanQuality: null`; it is excluded from the quality min-max and scores 0
 *     on quality (so it can only win on the cost end, never the pick at the
 *     performance end). `hasQualityData` lets the UI render "no quality data
 *     yet" — unknown to the operator, 0 to the math.
 */
import type { ModelPricingRow } from '@/lib/api'
import { FACET_CURATED } from '@/lib/agentModelSpec'

type CostCategory = NonNullable<ModelPricingRow['costCategory']>

/** One model going into the ranking. */
export interface RecoInput {
  namePattern: string
  /** Continuous cost basis in cents/1M: `input + 3×output`. The ranking uses
   * this; the UI only ever shows `costCategory`. */
  blendedCents: number
  /** Display pill only — never feeds the ranking. */
  costCategory?: CostCategory
  /** This agent's mean quality for the model (0–100), or `null` if never run. */
  meanQuality: number | null
  /** Runs behind `meanQuality`; 0 when the model has no history for this agent. */
  runCount: number
}

/** The structured "why" for one ranked model — numbers only; the UI formats. */
export interface RecoExplanation {
  meanQuality: number | null
  runCount: number
  /** 0–100, scaled within the candidate set; 0 when there's no quality data. */
  qualityScore: number
  /** 0–100 cost-efficiency, scaled within the set (cheapest = 100). */
  costScore: number
  /** 0–100 at the current slider weight. */
  combinedScore: number
  hasQualityData: boolean
}

export interface RankedModel {
  namePattern: string
  costCategory?: CostCategory
  explanation: RecoExplanation
}

/** Degenerate axis (one model, or all equal) → midpoint, avoiding divide-by-zero. */
const NEUTRAL = 50

/**
 * Rank `models` at slider weight `w ∈ [0,1]` (0 = cost-efficient, 1 =
 * performance). Deterministic: ties break by `meanQuality` desc, then
 * `namePattern` asc. `combined = w·quality + (1−w)·costEfficiency`, each axis
 * min-max scaled within `models`.
 */
export function rankModels(models: readonly RecoInput[], w: number): RankedModel[] {
  const clampedW = Math.min(1, Math.max(0, w))
  const costs = models.map((m) => m.blendedCents)
  const minCost = costs.length ? Math.min(...costs) : 0
  const maxCost = costs.length ? Math.max(...costs) : 0
  const scoredQ = models.map((m) => m.meanQuality).filter((v): v is number => v !== null)
  const minQ = scoredQ.length ? Math.min(...scoredQ) : 0
  const maxQ = scoredQ.length ? Math.max(...scoredQ) : 0

  const ranked: RankedModel[] = models.map((m) => {
    // Cost-efficiency: cheaper is better, so invert (priciest → 0, cheapest → 100).
    const costScore = maxCost <= minCost ? NEUTRAL : ((maxCost - m.blendedCents) / (maxCost - minCost)) * 100
    const q = m.meanQuality
    const hasQualityData = q !== null
    const qualityScore = q !== null ? (maxQ <= minQ ? NEUTRAL : ((q - minQ) / (maxQ - minQ)) * 100) : 0
    const combinedScore = clampedW * qualityScore + (1 - clampedW) * costScore
    return {
      namePattern: m.namePattern,
      costCategory: m.costCategory,
      explanation: { meanQuality: q, runCount: m.runCount, qualityScore, costScore, combinedScore, hasQualityData },
    }
  })

  ranked.sort(
    (a, b) =>
      b.explanation.combinedScore - a.explanation.combinedScore ||
      (b.explanation.meanQuality ?? -1) - (a.explanation.meanQuality ?? -1) ||
      (a.namePattern < b.namePattern ? -1 : a.namePattern > b.namePattern ? 1 : 0),
  )
  return ranked
}

/** The leaderboard subset this module reads — `ModelLeaderboardRow` satisfies it. */
export interface RecoLeaderboardRow {
  model: string
  meanQuality: number
  runCount: number
}

/**
 * Build one agent's ranking inputs: the **de-duplicated union** of the curated
 * pricing rows and the models this agent has actually run (the leaderboard),
 * each joined to pricing for the continuous cost basis. Quality comes from the
 * leaderboard (`null` when the model has no history). A model with no priced
 * catalog row is dropped — there's no cost basis to rank it on. Iterating the
 * catalog (unique by `namePattern`) makes the union de-dup itself: a model that
 * is both curated and in history appears once.
 */
export function buildRecoInputs(
  pricingRows: readonly ModelPricingRow[],
  leaderboard: readonly RecoLeaderboardRow[],
): RecoInput[] {
  const historyByModel = new Map(leaderboard.map((r) => [r.model, r]))
  const out: RecoInput[] = []
  for (const row of pricingRows) {
    if (row._unpriced) continue // no cost basis
    const lb = historyByModel.get(row.namePattern)
    const isCurated = row.collections?.includes(FACET_CURATED) ?? false
    if (!isCurated && lb === undefined) continue // outside curated ∪ history
    out.push({
      namePattern: row.namePattern,
      blendedCents: row.inputCentsPer1M + 3 * row.outputCentsPer1M,
      costCategory: row.costCategory,
      meanQuality: lb ? lb.meanQuality : null,
      runCount: lb ? lb.runCount : 0,
    })
  }
  return out
}
